import { CaseUtil } from '@vectord/utils';
import { type Edge, type Node } from '@xyflow/react';
import { EdgeType } from 'automations/data/edgeTypes';
import { combinedSubNodes, NodeType } from 'automations/data/nodeTypes';
import { ConditionTypes } from 'automations/types/automationConfig';

import { type GetAutomationExecutionResponse } from '../queries/getAutomationExecution';

const getNode = (nodeId: string, nodes: Node<any>[]) => nodes.find((node) => node?.data?.automationNode?.id === nodeId);

export function getCustomEdgeSourceHandle(nodeSubType: ConditionTypes, targetNodeId: string, nodes: Node[]) {
  const targetNode = getNode(targetNodeId, nodes);
  if (nodeSubType === ConditionTypes.ifElse && targetNode?.data?.executionOrder === 1) {
    return 'if';
  } else if (nodeSubType === ConditionTypes.ifElse && targetNode?.data?.executionOrder === 2) {
    return 'else';
  }
  return null;
}

export function getEdgeType(sourceNodeId: string, targetNodeId: string, nodes: Node[]) {
  const sourceNode = getNode(sourceNodeId, nodes);
  const targetNode = getNode(targetNodeId, nodes);
  if (sourceNode?.data?.nodeSubType === ConditionTypes.ifElse) {
    return targetNode?.data.executionOrder === 1 ? EdgeType.if : EdgeType.else;
  }

  return EdgeType.default;
}

export const parseAutomationExecution = (
  execution: GetAutomationExecutionResponse['automationExecutions']['automationExecutions'][0],
): { nodes: Node[]; edges: Edge[] } => {
  const nodeIdMapping = new Map(
    execution.executionNodes.map((execNode) => [execNode.automationNode.id, execNode.id]),
  );

  const nodes: Node[] = execution.executionNodes.map((execNode) => {
    const { nodeType, nodeSubType } = execNode.automationNode;
    const parsedNodeType = (
      combinedSubNodes.includes(nodeSubType as any)
      && Object.values(NodeType).includes(nodeType as any)
    ) ? nodeType : NodeType.fallback;

    let { additionalOptions } = CaseUtil.toCase('camelCase', execNode.automationNode);
    if (execNode.additionalOptions && Object.keys(execNode.additionalOptions).length > 0) {
      additionalOptions = CaseUtil.toCase('camelCase', execNode.additionalOptions);
    }

    return {
      id: execNode.id,
      type: parsedNodeType,
      position: { x: 0, y: 0 },
      data: {
        ...CaseUtil.toCase('camelCase', execNode.automationNode),
        ...CaseUtil.toCase('camelCase', execNode),
        nodeType: parsedNodeType,
        serverNodeType: nodeType,
        executionStatus: execNode.executionStatus,
        automationNodeId: execNode.automationNode.id,
        additionalOptions,
      },
    };
  });

  const edges: Edge[] = execution.executionNodes.flatMap(
    (execNode) => execNode.automationNode.sourceEdges.map((sourceEdge) => ({
      id: sourceEdge.id,
      source: nodeIdMapping.get(execNode.automationNode.id)!,
      target: nodeIdMapping.get(sourceEdge.targetNodeId.toString())!,
      sourceHandle: getCustomEdgeSourceHandle(
        execNode.automationNode.nodeSubType as ConditionTypes,
        sourceEdge.targetNodeId.toString(),
        nodes,
      ),
      type: getEdgeType(execNode.automationNode.id, sourceEdge.targetNodeId.toString(), nodes),
    })),
  );

  return { nodes, edges };
};
